import { toastController } from '@/controllers/toastController'
import { uiController } from '@/controllers/uiController'
import { PAGE_LINKS } from '@/helpers/commonConsts'
import { commonUtils } from '@/helpers/commonUtils'
import { envHelper } from '@/helpers/envHelper'
import { routerService } from '@/services/routerService'
import { userService } from '@/services/userService'
import { Divider } from 'antd'
import classNames from 'classnames'
import { observer } from 'mobx-react'
import { FC, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import style from './PageHeader.module.less'

interface PageHeaderProps {
  className?: string
  rightBoxVisible?: boolean
  isScrolling?: boolean
}

const PageHeader: FC<PageHeaderProps> = observer((props: PageHeaderProps) => {
  const { className, rightBoxVisible, isScrolling } = props
  const [showHelpBox, setShowHelpBox] = useState(false)

  useEffect(() => {
    document.body.addEventListener('click', () => {
      setShowHelpBox(false)
    })
  }, [])

  const handleLogin = (type: 'login' | 'register', redirectUrl?: string) => {
    if (!commonUtils.isInstalledExtension()) {
      routerService.navigateTo('install', { from: 'login' })
      return
    }
    if (redirectUrl) {
      routerService.navigateTo('login', { from: 'home', type, redirectUrl })
    } else {
      routerService.navigateTo('login', { from: 'home', type })
    }
  }
  return (
    <div className={classNames(style.pageHeader, className, { [style.hasScroll]: isScrolling })}>
      <div className={classNames(style.leftBox)}>
        <a href={PAGE_LINKS.dashboard} className={classNames('flex-row')}>
          {envHelper.oem === 'lenovo' ? (
            <img
              src={commonUtils.getStaticFile('cdn/home-page_icon__logo.png')}
              className={classNames(style.logo)}
            />
          ) : (
            <img
              src='https://assets.weibanzhushou.com/web/pt-web/home-page_icon__logo.1b9e042059a1.png'
              className={classNames(style.logo)}
            />
          )}
        </a>
        {rightBoxVisible && (
          <>
            <Link
              to={PAGE_LINKS.learning}
              className={style.linkBtn}
              onClick={() => {
                userService.recordLearningMode('click_header_learning')
              }}
              target='_blank'
            >
              生词本
            </Link>
            <Link
              className={classNames(style.linkBtn)}
              target='_blank'
              to='/invite'
              onClick={(e) => {
                if (!userService.isLogin) {
                  e.preventDefault()
                  handleLogin('login', encodeURIComponent(window.location.origin + '/invite'))
                }
              }}
            >
              <span className={classNames('flex-row', 'justify-center')}>
                <img
                  className={classNames(style.linkBtnIcon)}
                  style={{ marginRight: '4px' }}
                  src='https://assets.weibanzhushou.com/web/pt-web/invite-page_invite-gift.3cd481dca8f6.png'
                />
                <span>邀请有礼</span>
              </span>
            </Link>
            <Link to='/dashboard' target='_blank' className={style.linkBtn}>
              在线翻译
            </Link>
            <p
              className={style.linkBtn}
              onClick={() => {
                window.open('/dashboard/help_center?from=home', '_blank')
              }}
            >
              帮助中心
            </p>
            <div
              className={classNames(style.linkBtn, style.linkBtnHelp)}
              onClick={(e) => {
                e.stopPropagation()
                setShowHelpBox(!showHelpBox)
              }}
            >
              联系我们
              <div className={classNames(style.helpBox, { [style.visible]: showHelpBox })}>
                <img
                  className={classNames(style.qrCode)}
                  src='https://assets.weibanzhushou.com/web/pt-web/%E4%BC%9A%E8%AF%91-%E8%81%94%E7%B3%BB%E5%AE%A2%E6%9C%8D.126a016dd9f4.jpeg'
                />
              </div>
            </div>
            <Link className={classNames(style.linkBtn)} to='/pricing' target='_blank'>
              定价
            </Link>
          </>
        )}
      </div>
      <div
        className={classNames(style.rightBox, {
          [style.hide]: !rightBoxVisible || !userService.initialed,
        })}
      >
        <div
          className={classNames(style.dashboardBtn)}
          onClick={() => {
            routerService.openNewTab('dashboard')
          }}
        >
          翻译工具箱
        </div>
        <div
          className={classNames(style.translationBtn, 'flex-row', 'justify-center', {
            [style.oemLenovo]: envHelper.oem === 'lenovo',
          })}
          onClick={() => {
            routerService.openNewTab('pdf')
          }}
        >
          <p className={classNames(style.text)}>PDF 翻译</p>
        </div>
        <Divider type='vertical' style={{ margin: '0 20px', height: '20px', background: '#ccc' }} />
        {!userService.isLogin && (
          <>
            <div
              className={classNames(style.linkBtn)}
              onClick={() => {
                handleLogin('login')
              }}
              style={{
                marginLeft: 0,
              }}
            >
              登录
            </div>
            <div
              className={classNames(style.btn)}
              onClick={() => {
                handleLogin('register')
              }}
            >
              免费注册
            </div>
          </>
        )}
        {userService.isLogin && <UserInfoBox></UserInfoBox>}
      </div>
    </div>
  )
})

const UserInfoBox = observer(() => {
  const [showJumpToSetting, setShowJumpToSetting] = useState(false)
  useEffect(() => {
    checkPDFAndExtensionOptionPageVisible()
    commonUtils.asyncDelay(500).then(() => {
      checkPDFAndExtensionOptionPageVisible()
    })
    commonUtils.asyncDelay(2000).then(() => {
      checkPDFAndExtensionOptionPageVisible()
    })
    commonUtils.asyncDelay(3000).then(() => {
      checkPDFAndExtensionOptionPageVisible()
    })
  }, [])

  const checkPDFAndExtensionOptionPageVisible = () => {
    const show =
      commonUtils.isInstalledExtension() && !commonUtils.checkExtensionVersionShouldUpdate('1.1.0')
    setShowJumpToSetting(show)
  }
  const userInfo = userService.userData
  if (!userInfo) {
    return null
  }

  const { nickname, wxAvatar, inviteCode } = userInfo
  return (
    <div
      className={classNames(style.userInfoBox, {
        [style.oemLenovo]: envHelper.oem === 'lenovo',
      })}
    >
      {wxAvatar ? (
        <img className={classNames(style.avatar)} src={wxAvatar} />
      ) : (
        <img className={classNames(style.avatar)} src={commonUtils.getDefaultAvatar()} />
      )}
      <p className={classNames(style.nickname)}>
        {nickname ? nickname : `会译${inviteCode.slice(0, 4).toUpperCase()}`}
      </p>
      <img
        className={classNames(style.iconArrow)}
        src='https://assets.weibanzhushou.com/web/pt-web/page-header_triangle-icon.9d78d402ad4f.png'
      />
      <div className={classNames(style.menuBoxWrapper)}>
        <div className={classNames(style.menuBox)}>
          <div
            className={classNames(style.menuLi)}
            onClick={() => {
              uiController.exchangeDialogProps = { open: true }
            }}
          >
            <img
              className={classNames(style.icon)}
              src='https://assets.weibanzhushou.com/web/pt-web/page-header_icon__coupon.578fecf1565b.png'
            />
            <span className={classNames(style.text)}>兑换码</span>
          </div>
          <div
            className={classNames(style.menuLi)}
            onClick={async () => {
              await userService.logout()
              toastController.success('退出成功')
              await commonUtils.asyncDelay(1000)
              routerService.navigateTo('home')
            }}
          >
            <img
              className={classNames(style.icon)}
              src='https://assets.weibanzhushou.com/web/pt-web/page-header_icon__logout.0d3a4d0087e0.png'
            />
            <span className={classNames(style.text)}>退出登录</span>
          </div>
        </div>
      </div>
    </div>
  )
})

export default PageHeader
